const storeId = window.theme.store_id; // Store Id
const customerAccountClientId = window.theme.client_id; // customer account client id

async function accountLoginUrlGenerator() {
  const clientId = customerAccountClientId;
  const redirectUri = `${window.location.origin}`;
  const state = await generateState();
  const nonce = await generateNonce(16);
  const responseType = "code";
  // Public client
  let verifier = localStorage.getItem("code-verifier");
  if (!verifier) {
    verifier = await generateCodeVerifier();
    localStorage.setItem("code-verifier", verifier);
  }
  const challenge = await generateCodeChallenge(verifier);

  const authorizationRequestUrl = new URL(
    `https://shopify.com/authentication/${storeId}/oauth/authorize`,
  );

  authorizationRequestUrl.searchParams.append(
    "scope",
    "openid email customer-account-api:full",
  );

  authorizationRequestUrl.searchParams.append("client_id", clientId);

  authorizationRequestUrl.searchParams.append("response_type", responseType);

  authorizationRequestUrl.searchParams.append("redirect_uri", redirectUri);

  authorizationRequestUrl.searchParams.append("state", state);

  authorizationRequestUrl.searchParams.append("nonce", nonce);

  authorizationRequestUrl.searchParams.append("code_challenge", challenge);

  authorizationRequestUrl.searchParams.append("code_challenge_method", "S256");

  return authorizationRequestUrl.toString();
}

export const generateAccountLoginUrl = accountLoginUrlGenerator();

const ACCESS_DATA_KEY = "customer-access-data";
const EXPIRY_MARGIN_MS = 2 * 60 * 1000;

function readAccessData() {
  const customerAccessData = localStorage.getItem(ACCESS_DATA_KEY);
  return customerAccessData ? JSON.parse(customerAccessData) : null;
}

function isFresh(accessData) {
  return accessData?.expires_at > Date.now() + EXPIRY_MARGIN_MS;
}

function withRefreshLock(callback) {
  if (!navigator.locks) return callback();
  return navigator.locks.request("customer-access-token-refresh", callback);
}

export async function obtainAccessToken() {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");

  if (code) {
    // This is set in the accountLoginUrlGenerator
    const codeVerifier = localStorage.getItem("code-verifier");
    localStorage.removeItem("code-verifier");

    urlParams.delete("code");
    const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", newUrl);

    return requestAccessToken(
      new URLSearchParams({
        grant_type: "authorization_code",
        redirect_uri: window.location.origin,
        code,
        code_verifier: codeVerifier,
        client_id: customerAccountClientId,
      }),
      true,
    );
  }

  const accessData = readAccessData();

  if (!accessData?.refresh_token) {
    console.warn(
      "No code parameter found in the URL or refresh token found in local storage",
    );
    return { success: false };
  }

  if (isFresh(accessData)) return { success: true, initialLogin: false };

  return withRefreshLock(() => {
    // Another tab may have refreshed while this one waited for the lock,
    // spending the refresh token read above.
    const latest = readAccessData();

    if (isFresh(latest)) return { success: true, initialLogin: false };
    if (!latest?.refresh_token) return { success: false };

    return requestAccessToken(
      new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: latest.refresh_token,
        client_id: customerAccountClientId,
      }),
      false,
    );
  });
}

async function requestAccessToken(body, initialLogin) {
  try {
    const response = await fetch(
      `https://shopify.com/authentication/${storeId}/oauth/token`,
      {
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body,
      },
    );
    const data = await response.json();
    const { access_token, expires_in, id_token, refresh_token } = data;
    if (!data?.access_token) {
      throw new Error("No data returned from the token request");
    }
    // Customer access data is used to make requests to the customer account API
    localStorage.setItem(
      ACCESS_DATA_KEY,
      JSON.stringify({
        access_token,
        expires_in,
        expires_at: Date.now() + expires_in * 1000,
        id_token,
        refresh_token,
      }),
    );
    return { success: true, initialLogin };
  } catch (error) {
    if (error instanceof TypeError) {
      console.error("Network/CORS error in obtainAccessToken function:", error);
      return { success: false };
    }
    console.error("Error parsing response:", error);
    removeAllCustomerRelatedData();
    window.location.reload();
    return { success: false, initialLogin };
  }
}

export async function handleCustomerAccessToken() {
  const customerAccessData = localStorage.getItem("customer-access-data");
  const { access_token } = customerAccessData
    ? JSON.parse(customerAccessData)
    : { access_token: null };
  if (access_token) {
    try {
      const response = await fetch(
        `https://shopify.com/${storeId}/account/customer/api/2024-10/graphql`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${access_token}`,
          },
          body: JSON.stringify({
            operationName: "storefrontCustomerAccessTokenCreate",
            query: `mutation storefrontCustomerAccessTokenCreate {
              storefrontCustomerAccessTokenCreate {
                customerAccessToken
                userErrors {
                  field
                  message
                }
              }
            }`,
            variables: {},
          }),
        },
      );
      const data = await response.json();
      const customerAccessToken =
        data.data.storefrontCustomerAccessTokenCreate.customerAccessToken;
      // Customer access token is used to make requests to the storefront GraphQL API
      localStorage.setItem("customer-access-token", customerAccessToken);
      return customerAccessToken;
    } catch (error) {
      removeAllCustomerRelatedData();
      window.location.reload();
      console.error(
        "Error parsing response for customer access token in handleCustomerAccessToken function:",
        error,
      );
      return null;
    }
  }
  return null;
}

// After logging out, we need to remove all customer related data
export const removeAllCustomerRelatedData = () => {
  localStorage.removeItem("customer-access-token");
  localStorage.removeItem("customer-access-data");
};

/** Basic functions for fields noted in https://shopify.dev/docs/api/customer#step-authorization */

async function generateCodeVerifier() {
  const rando = generateRandomCode();
  return base64UrlEncode(rando);
}

async function generateCodeChallenge(codeVerifier) {
  const digestOp = await crypto.subtle.digest(
    { name: "SHA-256" },
    new TextEncoder().encode(codeVerifier),
  );
  const hash = convertBufferToString(digestOp);
  return base64UrlEncode(hash);
}

function generateRandomCode() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return String.fromCharCode.apply(null, Array.from(array));
}

function base64UrlEncode(str) {
  const base64 = btoa(str);
  // This is to ensure that the encoding does not have +, /, or = characters in it.
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function convertBufferToString(hash) {
  const uintArray = new Uint8Array(hash);
  const numberArray = Array.from(uintArray);
  return String.fromCharCode(...numberArray);
}

// Generating State
async function generateState() {
  const timestamp = Date.now().toString();
  const randomString = Math.random().toString(36).substring(2);
  return timestamp + randomString;
}

// Generating Nonce
async function generateNonce(length) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let nonce = "";

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    nonce += characters.charAt(randomIndex);
  }

  return nonce;
}

const removeStateFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete("state");
  const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", newUrl);
};

window.accountLoginUrlGenerator = accountLoginUrlGenerator;
